import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Button, Header, Icon, Message, Modal } from "semantic-ui-react";
import moment from "moment";
import { getApplicantByIdApi } from "../../../../../../../../../apis/applicantApi";
import {
  triggerEntryScreenAgentRunApi,
  getEntryScreenAgentRunStatusApi,
} from "../../../../../../../../../apis/entryScreenAgentApi";
import RunCard from "./RunCard.component";
import { isTerminalStatus, MAX_POLLS, POLL_INTERVAL_MS } from "./constants";

const DS160_API = "ds160";

// Normalize an Axios error into the backend's { code?, message, details? } error envelope,
// falling back to the raw message when the response did not carry one.
function toErrorPayload(error, fallbackMessage) {
  return (
    (error.response && error.response.data && error.response.data.error) || {
      message: error.message || fallbackMessage,
    }
  );
}

const byCreatedAtDesc = (a, b) => moment(b.createdAt).diff(moment(a.createdAt));

// The most recent run is the one with the latest createdAt.
function latestRunOf(list) {
  return [...list].sort(byCreatedAtDesc)[0];
}

function EntryScreenAgentModal({ applicantId, applicationCountry, initialRuns }) {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState(initialRuns || []);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState(null);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [statusByRun, setStatusByRun] = useState({});
  const [trackedRunId, setTrackedRunId] = useState(null);

  const isMountedRef = useRef(true);
  const pollCountRef = useRef(0);
  const statusByRunRef = useRef(statusByRun);

  useEffect(() => {
    statusByRunRef.current = statusByRun;
  }, [statusByRun]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  const sortedRuns = useMemo(() => [...runs].sort(byCreatedAtDesc), [runs]);

  const fetchRuns = useCallback(async () => {
    try {
      const { data } = await getApplicantByIdApi(
        applicantId,
        { populate: false },
        DS160_API,
      );
      const nextRuns = (data && data.entryScreenAgentRuns) || [];
      if (isMountedRef.current) {
        setRuns(nextRuns);
      }
      return nextRuns;
    } catch (error) {
      console.error("Failed to refresh Entry Screen Agent runs", error);
      return null;
    }
  }, [applicantId]);

  const fetchRunStatus = useCallback(
    async (runId) => {
      setStatusByRun((prev) => ({
        ...prev,
        [runId]: { ...prev[runId], loading: true, error: null },
      }));
      try {
        const { data: body } = await getEntryScreenAgentRunStatusApi(
          applicantId,
          runId,
        );
        if (isMountedRef.current) {
          setStatusByRun((prev) => ({
            ...prev,
            [runId]: { loading: false, data: body.data, error: null },
          }));
        }
      } catch (error) {
        const errorPayload = toErrorPayload(error, "Failed to load run status");
        if (isMountedRef.current) {
          setStatusByRun((prev) => ({
            ...prev,
            [runId]: {
              loading: false,
              data: prev[runId] && prev[runId].data,
              error: errorPayload,
            },
          }));
        }
      }
    },
    [applicantId],
  );

  // Auto-poll the tracked (latest) run until it reaches a terminal state, the
  // modal closes, or the ~5min cap is hit.
  useEffect(() => {
    if (!open || !trackedRunId) {
      return undefined;
    }
    pollCountRef.current = 0;
    fetchRunStatus(trackedRunId);

    const liveStatusOf = () => {
      const entry = statusByRunRef.current[trackedRunId];
      return entry && entry.data && entry.data.status;
    };

    const intervalId = setInterval(() => {
      pollCountRef.current += 1;
      if (isTerminalStatus(liveStatusOf()) || pollCountRef.current >= MAX_POLLS) {
        clearInterval(intervalId);
        return;
      }
      fetchRunStatus(trackedRunId);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [open, trackedRunId, fetchRunStatus]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setTriggerError(null);
    const latestRuns = await fetchRuns();
    if (!latestRuns || latestRuns.length === 0) {
      setTrackedRunId(null);
      return;
    }
    // Keep the recurring poll on the latest run only while it is still active.
    const latest = latestRunOf(latestRuns);
    const nextTrackedRunId =
      latest && !isTerminalStatus(latest.status) ? latest.runId : null;
    // First fetch: load every run's live status so each badge reflects the
    // backend, not the possibly-stale array status. The poll effect fetches the
    // tracked run itself, so skip it here to avoid a duplicate request.
    latestRuns.forEach((run) => {
      if (run.runId !== nextTrackedRunId) {
        fetchRunStatus(run.runId);
      }
    });
    setTrackedRunId(nextTrackedRunId);
  }, [fetchRuns, fetchRunStatus]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTrackedRunId(null);
  }, []);

  const handleTrigger = useCallback(async () => {
    setTriggering(true);
    setTriggerError(null);
    try {
      const { data: body } = await triggerEntryScreenAgentRunApi(applicantId);
      const newRunId = body.data && body.data.runId;
      await fetchRuns();
      if (newRunId) {
        setExpandedRunId(newRunId);
        setTrackedRunId(newRunId);
      }
    } catch (error) {
      setTriggerError(toErrorPayload(error, "Failed to trigger run"));
    } finally {
      if (isMountedRef.current) {
        setTriggering(false);
      }
    }
  }, [applicantId, fetchRuns]);

  const handleToggleExpand = useCallback(
    (runId) => {
      setExpandedRunId((prev) => {
        const next = prev === runId ? null : runId;
        const notLoaded = !statusByRunRef.current[runId];
        const notTracked = trackedRunId !== runId;
        if (next && notLoaded && notTracked) {
          fetchRunStatus(runId);
        }
        return next;
      });
    },
    [trackedRunId, fetchRunStatus],
  );

  return (
    <>
      <Button onClick={handleOpen}>Embassy AI Agent</Button>
      <Modal open={open} onClose={handleClose} size="fullscreen">
        <Modal.Header>Embassy AI Agent</Modal.Header>
        <Modal.Content scrolling>
          <Message info>
            <Message.Header>What this does</Message.Header>
            <p>
              Creates a usvisaappt.com scheduling account for this applicant on
              the embassy platform for{" "}
              <strong>{applicationCountry || "this applicant's country"}</strong>
              .
            </p>
            <p>
              <Icon name="warning sign" /> Each run creates a brand-new account.
              Triggering multiple times creates multiple accounts (there is no
              de-duplication).
            </p>
          </Message>

          {triggerError && (
            <Message negative>
              <Message.Header>Could not trigger run</Message.Header>
              <p>
                {triggerError.message}
                {triggerError.code ? ` (${triggerError.code})` : ""}
              </p>
              {Array.isArray(triggerError.details) &&
                triggerError.details.length > 0 && (
                  <ul>
                    {triggerError.details.map((detail, index) => (
                      <li key={`${index}-${detail}`}>{detail}</li>
                    ))}
                  </ul>
                )}
            </Message>
          )}

          <Button
            primary
            loading={triggering}
            disabled={triggering}
            onClick={handleTrigger}
          >
            <Icon name="play" /> Trigger run
          </Button>

          <Header as="h4" dividing style={{ marginTop: "1.5rem" }}>
            Runs
          </Header>
          {sortedRuns.length === 0 ? (
            <Message>
              <p>No runs yet. Trigger one above.</p>
            </Message>
          ) : (
            sortedRuns.map((run) => (
              <RunCard
                key={run.runId}
                run={run}
                expanded={expandedRunId === run.runId}
                live={statusByRun[run.runId]}
                onToggle={() => handleToggleExpand(run.runId)}
                polling={run.runId === trackedRunId}
              />
            ))
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleClose}>Close</Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

EntryScreenAgentModal.propTypes = {
  applicantId: PropTypes.string.isRequired,
  applicationCountry: PropTypes.string,
  initialRuns: PropTypes.arrayOf(
    PropTypes.shape({
      runId: PropTypes.string.isRequired,
      workflowId: PropTypes.string,
      status: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  ),
};

export default EntryScreenAgentModal;
